import toast from './toast';
import { formatDate, getTodayInSingaporeTimezone } from './dateUtils';

let jsPDF = null;
let autoTable = null;
let XLSX = null;

const loadDependencies = async () => {
  if (!jsPDF || !autoTable || !XLSX) {
    try {
      const jsPDFModule = await import('jspdf');
      const autoTableModule = await import('jspdf-autotable');
      const XLSXModule = await import('xlsx');
      jsPDF = jsPDFModule.default || jsPDFModule.jsPDF;
      autoTable = autoTableModule.default || autoTableModule.autoTable;
      XLSX = XLSXModule.default || XLSXModule;
    } catch (error) {
      console.error('Export dependencies not installed:', error);
      toast.error('Export packages unavailable. Please contact your administrator.');
      throw new Error('Export dependencies not available');
    }
  }
  return { jsPDF, autoTable, XLSX };
};

const EXPORT_COLUMNS = [
  { key: 'asset_tag', header: 'Asset Tag' },
  { key: 'category_name', header: 'Category' },
  { key: 'asset_type_name', header: 'Type', resolve: (a) => a.asset_type?.name || a.asset_type_name || '' },
  { key: 'brand', header: 'Brand' },
  { key: 'model', header: 'Model' },
  { key: 'serial_number', header: 'Serial No.' },
  { key: 'status_display', header: 'Status' },
  { key: 'condition_display', header: 'Condition' },
  {
    key: 'ownership_type_display',
    header: 'Ownership',
    resolve: (a) => a.ownership_type_display || a.ownership_type || '',
  },
  { key: 'current_assignee_name', header: 'Assigned To' },
  { key: 'current_assignee_staff_id', header: 'Staff ID' },
  { key: 'assigned_date', header: 'Assigned Date', resolve: (a) => formatDate(a.assigned_date) },
  { key: 'deployed_project_name', header: 'Deployed Project' },
  { key: 'deployed_work_site_name', header: 'Deployed Work Site' },
  { key: 'deployed_date', header: 'Deployed Date', resolve: (a) => formatDate(a.deployed_date) },
  { key: 'location', header: 'Location' },
  { key: 'purchase_date', header: 'Purchase Date', resolve: (a) => formatDate(a.purchase_date) },
  { key: 'warranty_end_date', header: 'Warranty End', resolve: (a) => formatDate(a.warranty_end_date) },
  { key: 'is_under_warranty', header: 'Under Warranty', resolve: (a) => a.is_under_warranty ? 'Yes' : 'No' },
  { key: 'vendor_name', header: 'Vendor' },
  { key: 'next_due_date', header: 'Next Due Date', resolve: (a) => formatDate(a.next_due_date) },
  { key: 'next_due_type', header: 'Next Due Type' },
  { key: 'age_in_years', header: 'Age (Years)', resolve: (a) => a.age_in_years != null ? Number(a.age_in_years).toFixed(1) : '' },
  { key: 'remarks', header: 'Remarks' },
  { key: 'created_at', header: 'Created', resolve: (a) => formatDate(a.created_at) },
];

const PDF_COLUMNS = [
  { key: 'asset_tag', header: 'Asset Tag' },
  { key: 'category_name', header: 'Category' },
  { key: 'type', header: 'Type', resolve: (a) => a.asset_type?.name || a.asset_type_name || '' },
  { key: 'brand_model', header: 'Brand / Model', resolve: (a) => [a.brand, a.model].filter(Boolean).join(' ') },
  { key: 'serial_number', header: 'Serial No.' },
  { key: 'status_display', header: 'Status' },
  { key: 'condition_display', header: 'Condition' },
  {
    key: 'ownership_type_display',
    header: 'Ownership',
    resolve: (a) => a.ownership_type_display || a.ownership_type || '',
  },
  { key: 'current_assignee_name', header: 'Assigned To' },
  { key: 'location_or_project', header: 'Location / Project', resolve: (a) => a.deployed_project_name || a.location || '' },
  { key: 'warranty_end_date', header: 'Warranty End', resolve: (a) => formatDate(a.warranty_end_date) },
  { key: 'next_due_date', header: 'Next Due', resolve: (a) => a.next_due_date ? `${formatDate(a.next_due_date)} (${a.next_due_type || ''})` : '' },
];

const resolveValue = (asset, col) => {
  if (col.resolve) return col.resolve(asset) ?? '';
  return asset[col.key] ?? '';
};

const buildFilterSummary = (filterLabels) => {
  if (!filterLabels || filterLabels.length === 0) return '';
  return filterLabels.join(' | ');
};

const makeFilename = (ext, filterLabels) => {
  const date = getTodayInSingaporeTimezone();
  const hasFilters = filterLabels && filterLabels.length > 0;
  return `assets-export${hasFilters ? '-filtered' : ''}-${date}.${ext}`;
};


// =============================================
// PDF Export
// =============================================
export const exportAssetsPDF = async (assets, filterLabels = []) => {
  try {
    const { jsPDF: JsPDF, autoTable: autoTableFn } = await loadDependencies();
    const doc = new JsPDF({ orientation: 'landscape' });

    doc.setFontSize(18);
    doc.text('Asset Inventory Report', 14, 18);

    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${formatDate(new Date())}`, 14, 26);
    doc.text(`Total Assets: ${assets.length}`, 14, 32);

    const filterSummary = buildFilterSummary(filterLabels);
    if (filterSummary) {
      doc.text(`Filters: ${filterSummary}`, 14, 38);
    }

    const startY = filterSummary ? 44 : 38;

    const tableRows = assets.map((asset) =>
      PDF_COLUMNS.map((col) => String(resolveValue(asset, col)))
    );

    autoTableFn(doc, {
      startY,
      head: [PDF_COLUMNS.map((c) => c.header)],
      body: tableRows,
      theme: 'grid',
      headStyles: { fillColor: [220, 38, 38], fontSize: 7, cellPadding: 2 },
      styles: { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
      columnStyles: {
        0: { cellWidth: 22 },
        3: { cellWidth: 32 },
        7: { cellWidth: 28 },
        10: { cellWidth: 30 },
      },
      margin: { left: 10, right: 10 },
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(7);
      doc.setTextColor(150);
      doc.text(`Page ${i} of ${pageCount}`, 285, 200, { align: 'right' });
      doc.text('HR Portal - Asset Inventory - Confidential', 14, 200);
    }

    doc.save(makeFilename('pdf', filterLabels));
    toast.success(`Exported ${assets.length} assets to PDF`);
  } catch (error) {
    if (error.message === 'Export dependencies not available') return;
    console.error('PDF export error:', error);
    toast.error('Failed to export PDF');
  }
};


// =============================================
// XLSX Export
// =============================================
export const exportAssetsXLSX = async (assets, filterLabels = []) => {
  try {
    const { XLSX: XlsxLib } = await loadDependencies();

    const rows = assets.map((asset) => {
      const row = {};
      EXPORT_COLUMNS.forEach((col) => {
        row[col.header] = resolveValue(asset, col);
      });
      return row;
    });

    const ws = XlsxLib.utils.json_to_sheet(rows);
    ws['!cols'] = EXPORT_COLUMNS.map((col) => ({
      wch: Math.max(col.header.length + 2, 14),
    }));

    const wb = XlsxLib.utils.book_new();
    XlsxLib.utils.book_append_sheet(wb, ws, 'Assets');

    if (filterLabels.length > 0) {
      const metaRows = [
        { Field: 'Report', Value: 'Asset Inventory Export' },
        { Field: 'Generated', Value: formatDate(new Date()) },
        { Field: 'Total Assets', Value: String(assets.length) },
        { Field: 'Filters', Value: buildFilterSummary(filterLabels) },
      ];
      const metaWs = XlsxLib.utils.json_to_sheet(metaRows);
      metaWs['!cols'] = [{ wch: 16 }, { wch: 60 }];
      XlsxLib.utils.book_append_sheet(wb, metaWs, 'Export Info');
    }

    XlsxLib.writeFile(wb, makeFilename('xlsx', filterLabels));
    toast.success(`Exported ${assets.length} assets to Excel`);
  } catch (error) {
    if (error.message === 'Export dependencies not available') return;
    console.error('XLSX export error:', error);
    toast.error('Failed to export Excel file');
  }
};


// =============================================
// CSV Export
// =============================================
export const exportAssetsCSV = (assets, filterLabels = []) => {
  try {
    const escapeCSV = (val) => {
      const str = String(val ?? '');
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const headerRow = EXPORT_COLUMNS.map((c) => escapeCSV(c.header)).join(',');
    const dataRows = assets.map((asset) =>
      EXPORT_COLUMNS.map((col) => escapeCSV(resolveValue(asset, col))).join(',')
    );

    const csvContent = [headerRow, ...dataRows].join('\r\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = makeFilename('csv', filterLabels);
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
    toast.success(`Exported ${assets.length} assets to CSV`);
  } catch (error) {
    console.error('CSV export error:', error);
    toast.error('Failed to export CSV');
  }
};
